import { Injectable } from '@angular/core';
import { Invoice, InvoiceNotification, InvoiceProfessional, InvoiceService, InvoiceUnit, RevenueInvoiceEvent } from '../../domain/entities/invoice.entity';
import { InvoiceStatus } from '../../domain/enums/invoice-status.enum';
import { InvoiceDto, ViewAddressDto, ViewInvoiceNotificationDto, ViewProfessionalDto, ViewRevenueInvoiceEventDto, ViewServiceDto, ViewUnitDto } from '../dtos/invoice.dto';

@Injectable()
export class InvoiceMapper {
  fromDto(dto: InvoiceDto): Invoice {
    const status = this.toStatus(dto.status);
    const revenue = this.toRevenue(dto.revenue);
    const revenueId = revenue?.id ?? this.extractIdFromIri(typeof dto.revenue === 'string' ? dto.revenue : dto.revenue?.['@id']) ?? 0;
    const revenueValue = revenue?.revenue?.value;
    const revenueDate = revenue?.revenue?.date ?? dto.revenueDate ?? null;
    const amount = dto.effectiveAmount ?? dto.calculatedAmount ?? revenueValue ?? dto.revenueAmount ?? 0;
    const grossAmount = dto.revenueAmount ?? revenueValue ?? amount;
    const issuerType = this.toIssuerType(dto.issuerType);
    const professional = this.toProfessional(dto.professional);
    const unit = this.toUnit(dto.unit);
    const service = this.toService(dto.service);
    const payer = {
      id: dto.payer?.id ?? null,
      name: this.valueOrDash(dto.payer?.name),
      taxId: this.valueOrDash(dto.payer?.taxId)
    };
    const issuer = this.toIssuer(issuerType, professional, unit);
    const notifications = this.toNotifications(dto.invoiceNotifications);
    const technicalMessage = dto.invoiceNotifications?.[0]?.errorMessage
      ?? dto.invoiceNotifications?.[0]?.message
      ?? dto.invoiceNotifications?.[0]?.reason
      ?? null;

    return {
      id: dto.serviceInvoiceId,
      serviceInvoiceId: dto.serviceInvoiceId,
      revenueId,
      revenue,
      number: String(dto.invoiceNumber ?? revenueId),
      status,
      issuerType,
      professional,
      unit,
      payer,
      service,
      notifications,
      customer: {
        name: payer.name,
        taxId: payer.taxId
      },
      professionalName: professional?.name ?? '-',
      unitName: unit?.tradeName || unit?.legalName || unit?.unitName || '-',
      serviceName: service?.name ?? '-',
      specialtyName: service?.specialty?.name ?? '-',
      issueDate: revenueDate,
      dueDate: null,
      canCancel: this.canCancel(dto.issuedOn, status),
      issuer: {
        id: dto.serviceInvoiceId,
        name: issuer.name,
        registrationNumber: issuer.registrationNumber,
        status,
        amount,
        type: issuerType
      },
      technicalMessage,
      friendlyErrorMessage: this.toFriendlyErrorMessage(status, technicalMessage),
      financial: {
        grossAmount,
        unitAmount: issuerType === 'unit' ? amount : 0,
        professionalAmount: issuerType === 'professional' ? amount : 0,
        taxesAmount: Math.max(grossAmount - amount, 0)
      },
      address: this.toAddress(this.pickAddress(dto.address))
    };
  }

  toDto(model: Invoice): InvoiceDto {
    return {
      id: model.id,
      serviceInvoiceId: model.serviceInvoiceId,
      status: model.status,
      revenueDate: model.issueDate ?? undefined,
      issuerType: model.issuer.type
    };
  }

  private canCancel(issuedOn: string | undefined, status: InvoiceStatus): boolean {
    if (status !== InvoiceStatus.Issued && status !== InvoiceStatus.Draft) {
      return false;
    }

    if (!issuedOn) {
      return true;
    }

    const diffInHours = (Date.now() - new Date(issuedOn).getTime()) / (1000 * 60 * 60);
    return diffInHours <= 24;
  }

  private toStatus(status?: string): InvoiceStatus {
    const normalizedStatus = this.normalizeStatus(status);

    return Object.values(InvoiceStatus).includes(normalizedStatus as InvoiceStatus)
      ? normalizedStatus as InvoiceStatus
      : InvoiceStatus.Unknown;
  }

  private normalizeStatus(status?: string): string | undefined {
    if (!status) return status;

    const normalized = status
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const legacyStatusMap: Record<string, InvoiceStatus> = {
      falha: InvoiceStatus.Failed,
      pendente: InvoiceStatus.Pending,
      cancelado: InvoiceStatus.Canceled,
      cancelada: InvoiceStatus.Canceled,
    };

    return legacyStatusMap[normalized] ?? status;
  }

  private toIssuerType(type?: string): 'unit' | 'professional' {
    return type === 'professional' ? 'professional' : 'unit';
  }

  private toRevenue(revenue?: string | ViewRevenueInvoiceEventDto): RevenueInvoiceEvent | null {
    if (!revenue || typeof revenue === 'string') {
      const id = this.extractIdFromIri(revenue);
      return id ? { id, revenue: null } : null;
    }

    return {
      id: revenue.id ?? this.extractIdFromIri(revenue['@id']) ?? 0,
      revenue: revenue.revenue
        ? {
            date: revenue.revenue.date ?? null,
            value: revenue.revenue.value ?? 0
          }
        : null
    };
  }

  private toProfessional(professional?: ViewProfessionalDto): InvoiceProfessional | null {
    if (!professional) return null;

    return {
      id: professional.id ?? null,
      name: this.valueOrDash(professional.name),
      issueInvoice: professional.issueInvoice ?? false,
      company: professional.company
        ? {
            id: professional.company.id ?? null,
            legalName: professional.company.legalName ?? '',
            registrationNumber: professional.company.registrationNumber ?? '',
            tradeName: professional.company.tradeName ?? ''
          }
        : null
    };
  }

  private toUnit(unit?: ViewUnitDto): InvoiceUnit | null {
    if (!unit) return null;

    return {
      id: unit.id ?? null,
      unitName: unit.unitName ?? '',
      legalName: unit.legalName ?? '',
      registrationNumber: unit.registrationNumber ?? '',
      tradeName: unit.tradeName ?? ''
    };
  }

  private toService(service?: ViewServiceDto): InvoiceService | null {
    if (!service) return null;

    const specialtyName = typeof service.specialty === 'string'
      ? ''
      : service.specialty?.name ?? '';

    return {
      id: service.id ?? null,
      name: this.valueOrDash(service.name),
      specialty: specialtyName ? { name: specialtyName } : null,
      value: service.value ?? 0,
      transferValue: service.transferValue ?? 0,
      transferPercentage: service.transferPercentage ?? 0
    };
  }

  private toIssuer(
    issuerType: 'unit' | 'professional',
    professional: InvoiceProfessional | null,
    unit: InvoiceUnit | null
  ): { name: string; registrationNumber: string } {
    if (issuerType === 'professional') {
      return {
        name: this.valueOrDash(
          professional?.company?.legalName ||
          professional?.company?.tradeName ||
          professional?.name
        ),
        registrationNumber: this.valueOrDash(professional?.company?.registrationNumber)
      };
    }

    return {
      name: this.valueOrDash(unit?.legalName || unit?.tradeName || unit?.unitName),
      registrationNumber: this.valueOrDash(unit?.registrationNumber)
    };
  }

  private toNotifications(notifications?: readonly ViewInvoiceNotificationDto[]): readonly InvoiceNotification[] {
    return (notifications ?? []).map((notification) => ({
      message: notification.message ?? null,
      errorMessage: notification.errorMessage ?? null,
      reason: notification.reason ?? null
    }));
  }

  private extractIdFromIri(iri?: string): number | undefined {
    if (!iri) return undefined;
    const match = iri.match(/(\d+)$/);
    return match ? Number(match[1]) : undefined;
  }

  private pickAddress(address?: ViewAddressDto | ViewAddressDto[]): ViewAddressDto | null {
    return Array.isArray(address) ? address[0] ?? null : address ?? null;
  }

  private toAddress(address?: ViewAddressDto | null): Invoice['address'] {
    return {
      street: address?.streetAddress ?? '',
      number: address?.number ?? '',
      neighborhood: address?.neighborhood ?? address?.district ?? '',
      postalCode: address?.postalCode ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      complement: address?.address2 ?? '',
      email: address?.email ?? ''
    };
  }

  private valueOrDash(value: string | null | undefined): string {
    const normalized = String(value ?? '').trim();
    return normalized.length > 0 ? normalized : '-';
  }

  private toFriendlyErrorMessage(status: InvoiceStatus, technicalMessage: string | null): string | null {
    if (status !== InvoiceStatus.Failed && status !== InvoiceStatus.RegisterError && status !== InvoiceStatus.Error) {
      return null;
    }

    if (!technicalMessage) {
      return status === InvoiceStatus.RegisterError
        ? 'Não foi possível concluir o registro da nota. Acione o suporte para revisão.'
        : 'A emissão não foi concluída. Revise os dados fiscais e tente reemitir.';
    }

    if (technicalMessage.toLowerCase().includes('cnpj') || technicalMessage.toLowerCase().includes('cpf')) {
      return 'Documento fiscal inconsistente. Confira CPF/CNPJ do emissor ou consumidor.';
    }

    if (technicalMessage.toLowerCase().includes('address') || technicalMessage.toLowerCase().includes('endereco')) {
      return 'Endereço fiscal incompleto. Revise os dados cadastrais antes de reemitir.';
    }

    return 'A emissão encontrou uma inconsistência fiscal. Revise os dados e tente novamente.';
  }
}
